import AppForms from "@toothfairy/shared-ui/AppForms";
import { AppText } from "@toothfairy/shared-ui";
import AppTheme from "@toothfairy/shared-ui/AppTheme";
import React from "react";
import { useTranslation } from "react-i18next";
import { Platform, View } from "react-native";
import Constants from "../../../Constants";
import countries from "../../../Constants/countries.json";
import { TAX_ID_TYPES } from "../../../Constants/billing";
import useSections from "../../../Hooks/useSections";
import { isLargeScreen } from "../../MainViewContainer";

// Renders the business-only fields (business name + tax ID) only when the
// "isBusiness" form value is true. Uses Formik context to stay in sync with the
// switch without an AppFormConditional component.
const BusinessFields = ({ t, currentTheme, isLargeScreenVal }) => {
  const { values } = AppForms.useFormikContext();
  if (!values?.isBusiness) return null;
  return (
    <>
      <AppForms.AppFormField
        showTextInputLabel
        label={t("billing.form.businessName")}
        isMandatory
        name="businessName"
      />

      <AppForms.AppFormDropDown
        isElevated={false}
        dropDownStyle={{
          borderColor: currentTheme?.theme?.border_color,
          maxWidth: !isLargeScreenVal && 250,
        }}
        inputLabelStyle={{
          marginLeft: 5,
          marginBottom: 5,
          marginTop: -5,
        }}
        showTextInputLabel
        label={t("billing.form.taxIdType")}
        name="taxIdType"
        values={TAX_ID_TYPES}
        items={[
          { label: t("billing.form.selectTaxIdType"), value: "" },
          ...TAX_ID_TYPES.map((ti) => ({ label: ti.label, value: ti.value })),
        ]}
      />

      <AppForms.AppFormField
        showTextInputLabel
        label={t("billing.form.taxIdValue")}
        name="taxIdValue"
        placeholder={t("billing.form.taxIdValuePlaceholder")}
      />
    </>
  );
};

const BillingAddress = ({
  stripeCustomerData,
  saveCustomerDetails,
  isWizard,
  saveInProgress,
  goToPortal,
}) => {
  const { t } = useTranslation();
  const { currentTheme } = AppTheme.useTheme();

  // Read the business fields from the Stripe customer. Prefer the top-level
  // fields the backend exposes, but fall back to parsing the metadata JSON
  // strings so the form still populates before/without a backend redeploy.
  const md = stripeCustomerData?.metadata || {};
  let parsedTaxIdFromMeta = null;
  try {
    parsedTaxIdFromMeta = md.taxId ? JSON.parse(md.taxId) : null;
  } catch (e) {
    parsedTaxIdFromMeta = null;
  }
  const isBusinessInit =
    stripeCustomerData?.isBusiness === true || md.isBusiness === "true";
  const businessNameInit =
    stripeCustomerData?.businessName ?? md.businessName ?? "";
  const taxIdInit = stripeCustomerData?.taxId ||
    parsedTaxIdFromMeta || { type: "au_abn", value: "" };

  const addressFormValidationSchema = AppForms.yup.object().shape({
    name: AppForms.yup.string().required().label(t("billing.form.name")),
    addressLine1: AppForms.yup
      .string()
      .required()
      .label(t("billing.form.address")),
    addressLine2: AppForms.yup.string().nullable(),
    postalCode: AppForms.yup
      .string()
      .required()
      .label(t("billing.form.postalCode")),
    city: AppForms.yup.string().required().label(t("billing.form.city")),
    state: AppForms.yup.string().nullable().label(t("billing.form.state")),
    country: AppForms.yup
      .string()
      .required()
      .notOneOf([""], t("billing.form.selectCountry"))
      .label(t("billing.form.country")),
    isBusiness: AppForms.yup.boolean(),
    businessName: AppForms.yup
      .string()
      .nullable()
      .when("isBusiness", {
        is: true,
        then: (s) => s.required().label(t("billing.form.businessName")),
      }),
    taxIdType: AppForms.yup.string().nullable(),
    taxIdValue: AppForms.yup.string().nullable(),
  });

  return (
    <AppForms.AppForm
      style={{
        minWidth: Platform.OS === "web" && isLargeScreen() ? 350 : 250,
        maxWidth: !isLargeScreen() && 250,
        alignSelf: !isLargeScreen() && "center",
      }}
      initialValues={{
        name: stripeCustomerData?.name || "",
        addressLine1: stripeCustomerData?.address?.line1 || "",
        addressLine2: stripeCustomerData?.address?.line2 || "",
        postalCode: stripeCustomerData?.address?.postal_code || "",
        city: stripeCustomerData?.address?.city || "",
        state: stripeCustomerData?.address?.state || "",
        country: stripeCustomerData?.address?.country || "",
        isBusiness: isBusinessInit,
        businessName: businessNameInit,
        taxIdType: taxIdInit?.type || "au_abn",
        taxIdValue: taxIdInit?.value || "",
      }}
      enableReinitialize
      onSubmit={saveCustomerDetails}
      validationSchema={addressFormValidationSchema}
    >
      <AppForms.AppFormField
        showTextInputLabel
        label={t("billing.form.name")}
        isMandatory
        name="name"
      />

      <AppForms.AppFormField
        showTextInputLabel
        label={t("billing.form.address")}
        isMandatory
        name="addressLine1"
      />

      <AppForms.AppFormField name="addressLine2" />
      <View
        style={{
          flexDirection: "row",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <AppForms.AppFormField
          formFieldContainerStyle={{
            flex: 1,
          }}
          showTextInputLabel
          label={t("billing.form.postalCode")}
          isMandatory
          name="postalCode"
        />
        <AppForms.AppFormField
          formFieldContainerStyle={{
            flex: 1,
          }}
          showTextInputLabel
          label={t("billing.form.state")}
          isMandatory
          name="state"
        />
      </View>
      <View
        style={{
          flexDirection: "row",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <AppForms.AppFormField
          formFieldContainerStyle={{
            flex: 1,
            paddingBottom: 0,
          }}
          showTextInputLabel
          label={t("billing.form.city")}
          isMandatory
          name="city"
        />

        <AppForms.AppFormDropDown
          formFieldContainerStyle={{
            flex: 1,
          }}
          isElevated={false}
          dropDownStyle={{
            borderColor: currentTheme?.theme?.border_color,
            maxWidth: !isLargeScreen() && 250,
          }}
          showTextInputLabel
          label={t("billing.form.country")}
          isMandatory
          name="country"
          values={countries}
          items={[
            { label: t("billing.form.selectCountry"), value: "" },
            ...Object.entries(countries).map(([key, value]) => ({
              label: value,
              value: key,
            })),
          ]}
        />
      </View>

      {/* Business billing details: a toggle reveals the legal entity name and a
          tax identifier (ABN / VAT / EIN etc.) that is attached to the Stripe
          customer so it appears on invoices. International coverage. */}
      <View
        style={{
          flexDirection: "row",
          alignItems: "center",
          justifyContent: "space-between",
          marginTop: 16,
          marginBottom: 4,
          marginLeft: 10,
        }}
      >
        <AppText style={{ fontWeight: "bold", flex: 1, flexWrap: "wrap" }}>
          {t("billing.form.isBusiness")}
        </AppText>
        <AppForms.AppFormSwitch
          name="isBusiness"
          wrapperStyle={{ paddingVertical: 0 }}
        />
      </View>
      <AppText
        style={{
          fontSize: 12,
          color:
            currentTheme?.theme?.secondary_font_color ||
            currentTheme?.theme?.font_color,
          marginBottom: 8,
          marginLeft: 10,
        }}
      >
        {t("billing.form.isBusinessHint")}
      </AppText>

      <BusinessFields
        t={t}
        currentTheme={currentTheme}
        isLargeScreenVal={isLargeScreen()}
      />

      <AppForms.AppSubmitButton
        isDisabled={saveInProgress}
        isEnabledOnFilled={isWizard}
        awesomeIconColor={currentTheme?.theme?.white}
        backgroundColor={currentTheme?.theme?.primary}
        btnContentStyle={{
          paddingLeft: 0,
          paddingRight: 0,
        }}
        style={[
          {
            height: Constants.appButtonHeight,
            width: "auto",
            shadowColor: currentTheme?.theme?.transparent,
            marginHorizontal: 10,
          },
        ]}
        // btnLabelStyle={{ fontSize: 16 }}
        wrapperStyle={[
          {
            width: "fit-content",
            minWidth: 150,
            marginTop: 20,
            height: Constants.appButtonHeight,
            alignSelf: "center",
          },
        ]}
        title={isWizard ? t("billing.form.next") : t("billing.form.save")}
      />
    </AppForms.AppForm>
  );
};

export default BillingAddress;
